<ion-header>
  <ion-toolbar>
    <ion-buttons slot="start">
      <ion-back-button></ion-back-button>
    </ion-buttons>
    <ion-buttons slot="end">
      <ion-button *ngIf="isOwner" (click)="openEditModal()" aria-label="Edit playlist">
        <ion-icon name="pencil-outline" slot="icon-only"></ion-icon>
      </ion-button>
      <ion-button *ngIf="!isOwner && currentUser" (click)="togglePin()" aria-label="Bookmark playlist">
        <ion-icon [name]="isPinned ? 'bookmark' : 'bookmark-outline'" slot="icon-only"></ion-icon>
      </ion-button>
      <ion-button (click)="sharePlaylist()" aria-label="Share playlist">
        <ion-icon name="share-outline" slot="icon-only"></ion-icon>
      </ion-button>
    </ion-buttons>
  </ion-toolbar>
</ion-header>

<ion-content>
  <ion-refresher slot="fixed" (ionRefresh)="doRefresh($event)">
    <ion-refresher-content></ion-refresher-content>
  </ion-refresher>

  <div class="page-layout"
    [class.notes-open]="openNotesSong || openCommentsSong"
    [class.lyrics-open]="openLyricsSong">

    <!-- Songs column -->
    <div class="songs-column">

      <!-- Playlist header -->
      <div class="playlist-header">
        <h1 class="playlist-header-title">{{ playlistName }}</h1>
        <p *ngIf="playlist?.createdBy" class="playlist-header-byline">
          {{ 'PLAYLISTS.BY' | translate }}
          {{ playlist?.createdByUid ? (displayNames[playlist!.createdByUid!] || playlist?.createdBy) : playlist?.createdBy }}
        </p>
        <p *ngIf="playlist?.description" class="playlist-header-description">{{ playlist!.description }}</p>
      </div>

      <div class="search-wrapper">
        <ion-searchbar
          [placeholder]="'PLAYLISTS.SONG_SEARCH_PLACEHOLDER' | translate"
          [value]="searchQuery"
          (ionInput)="onSearch($event)"
          (ionClear)="onSearch({detail:{value:''}})"
          debounce="150"
          animated>
        </ion-searchbar>
      </div>

      <div *ngIf="loading" class="loading-container">
        <ion-spinner></ion-spinner>
      </div>

      <p *ngIf="!loading && filteredSongs.length === 0 && searchQuery" class="no-results">
        {{ 'PLAYLISTS.NO_SONGS' | translate:{query: searchQuery} }}
      </p>

      <!-- Spotify discovery results — shown when user types and has submit permission -->
      <div *ngIf="canSubmit && searchQuery.trim()" class="spotify-results-section">
        <div class="spotify-results-header">
          <span class="spotify-results-label">
            <ion-icon name="musical-notes-outline"></ion-icon>
            Add from Spotify
          </span>
          <ion-spinner *ngIf="spotifyLoading" name="crescent" class="spotify-spinner"></ion-spinner>
        </div>

        <div *ngIf="!spotifyLoading && spotifyResults.length === 0" class="spotify-empty">
          No Spotify matches found
        </div>

        <div *ngFor="let track of spotifyResults" class="spotify-track-row">
          <img
            *ngIf="spotifyAlbumThumb(track)"
            [src]="spotifyAlbumThumb(track)"
            [alt]="track.album.name"
            class="spotify-album-art"
            loading="lazy" />
          <div *ngIf="!spotifyAlbumThumb(track)" class="spotify-album-art spotify-album-placeholder">
            <ion-icon name="musical-notes-outline"></ion-icon>
          </div>
          <div class="spotify-track-info">
            <span class="spotify-track-name">{{ track.name }}</span>
            <span class="spotify-track-artist">{{ spotifyArtistNames(track) }}</span>
          </div>
          <ion-button fill="outline" size="small" class="spotify-add-btn" (click)="openSubmitModalForTrack(track)">
            <ion-icon name="add-outline" slot="start"></ion-icon>
            Add Song
          </ion-button>
        </div>
      </div>

      <div *ngIf="!loading" class="songs-list">
        <div *ngFor="let song of filteredSongs; trackBy: trackBySongId" class="song-card"
          [class.notes-active]="openNotesSong?.id === song.id"
          [class.comments-active]="openCommentsSong?.id === song.id"
          [class.lyrics-active]="openLyricsSong?.id === song.id">
          <ion-item lines="none">
            <ion-label>
              <h2 *ngIf="song.title" class="song-title">{{ song.title }}</h2>
              <p *ngIf="song.channelName" class="channel-name">{{ song.channelName }}</p>
              <p *ngIf="song.submittedByUid || song.submittedBy" class="submitted-by">
                {{ 'PLAYLISTS.SUBMITTED_BY' | translate }} {{ song.submittedByUid ? (displayNames[song.submittedByUid] || song.submittedBy) : song.submittedBy }}
              </p>
            </ion-label>
            <ion-button *ngIf="canEditSong(song)" fill="clear" color="medium" slot="end" (click)="editSong(song)" aria-label="Edit song">
              <ion-icon name="pencil-outline" slot="icon-only"></ion-icon>
            </ion-button>
            <ion-button *ngIf="canRemoveSong(song)" fill="clear" color="danger" slot="end" (click)="removeSong(song)" aria-label="Remove song">
              <ion-icon name="trash-outline" slot="icon-only"></ion-icon>
            </ion-button>
          </ion-item>

          <!-- Thumbnail (default) — tap to load iframe -->
          <div *ngIf="expandedSongId !== song.id" class="thumbnail-container" (click)="toggleExpand(song.id!)">
            <img [src]="getThumbnailUrl(song)" [alt]="song.title || 'YouTube video'" class="thumbnail" loading="lazy" />
            <div class="play-overlay">
              <div class="play-button">
                <ion-icon name="play" class="play-icon"></ion-icon>
              </div>
            </div>
          </div>

          <!-- Iframe (expanded on tap) -->
          <div *ngIf="expandedSongId === song.id" class="video-container">
            <iframe
              [id]="getMainPlayerElementId(song.id!)"
              [src]="getEmbedUrl(song.youtubeUrl)"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen>
            </iframe>
          </div>

          <ion-item lines="full" class="vote-row">
            <ng-container *ngIf="canVote; else voteDisabled">
              <ion-button fill="clear"
                (click)="vote(song, 'upvotes')"
                [disabled]="isOwnContribution(song)"
                [class.voted-up]="userVotes[song.id!] === 'up'">
                <ion-icon [name]="userVotes[song.id!] === 'up' ? 'thumbs-up' : 'thumbs-up-outline'" slot="start"></ion-icon>
                {{ song.upvotes }}
              </ion-button>
              <ion-button fill="clear"
                (click)="vote(song, 'downvotes')"
                [disabled]="isOwnContribution(song)"
                [class.voted-down]="userVotes[song.id!] === 'down'">
                <ion-icon [name]="userVotes[song.id!] === 'down' ? 'thumbs-down' : 'thumbs-down-outline'" slot="start"></ion-icon>
                {{ song.downvotes }}
              </ion-button>
            </ng-container>
            <ng-template #voteDisabled>
              <span class="vote-count">
                <ion-icon name="thumbs-up-outline"></ion-icon> {{ song.upvotes }}
              </span>
              <span class="vote-count">
                <ion-icon name="thumbs-down-outline"></ion-icon> {{ song.downvotes }}
              </span>
            </ng-template>

            <!-- Lyrics button — always visible -->
            <ion-button fill="clear" slot="end" class="lyrics-btn"
              [class.lyrics-btn-active]="openLyricsSong?.id === song.id"
              (click)="openLyrics(song)" aria-label="Lyrics">
              <ion-icon name="musical-notes-outline" slot="icon-only"></ion-icon>
            </ion-button>
            <!-- Notes button — only when song has notes -->
            <ion-button *ngIf="song.note" fill="clear" slot="end" class="notes-btn"
              [class.notes-btn-active]="openNotesSong?.id === song.id"
              (click)="openNotes(song)" aria-label="Song notes">
              <ion-icon name="document-text-outline" slot="icon-only"></ion-icon>
            </ion-button>
            <!-- Comment button — always visible -->
            <ion-button fill="clear" slot="end" class="comment-btn"
              [class.comment-btn-active]="openCommentsSong?.id === song.id"
              (click)="openComments(song)" aria-label="Comments">
              <ion-icon name="chatbubble-outline" slot="start"></ion-icon>
              {{ (song.commentCount ?? 0) > 0 ? song.commentCount : 0 }}
            </ion-button>
          </ion-item>
        </div>
      </div>
    </div>

    <!-- Notes panel -->
    <div *ngIf="openNotesSong" class="notes-panel">
      <div class="notes-panel-header">
        <div class="notes-panel-title">
          <ion-icon name="document-text-outline"></ion-icon>
          <span>{{ 'PLAYLISTS.SONG_NOTES' | translate }}</span>
        </div>
        <ion-button fill="clear" (click)="closeNotes()" aria-label="Close notes">
          <ion-icon name="close-outline" slot="icon-only"></ion-icon>
        </ion-button>
      </div>
      <p class="notes-song-title">{{ openNotesSong.title }}</p>
      <div class="notes-body markdown-content" [innerHTML]="renderedNote"></div>
    </div>

    <!-- Comments panel -->
    <div *ngIf="openCommentsSong" class="notes-panel comments-panel">
      <div class="notes-panel-header">
        <div class="notes-panel-title">
          <ion-icon name="chatbubble-outline"></ion-icon>
          <span>{{ 'PLAYLISTS.COMMENTS' | translate }}</span>
        </div>
        <ion-button fill="clear" (click)="closeComments()" aria-label="Close comments">
          <ion-icon name="close-outline" slot="icon-only"></ion-icon>
        </ion-button>
      </div>
      <p class="notes-song-title">{{ openCommentsSong.title }}</p>

      <!-- Input box -->
      <div *ngIf="canComment" class="comment-input-wrapper">
        <ion-textarea
          [(ngModel)]="newCommentText"
          [placeholder]="'PLAYLISTS.ADD_COMMENT_PLACEHOLDER' | translate"
          [autoGrow]="true"
          rows="2"
          maxlength="1000"
          class="comment-textarea">
        </ion-textarea>
        <ion-button expand="block" size="small" [disabled]="!newCommentText.trim()" (click)="submitComment()">
          {{ 'PLAYLISTS.POST_COMMENT' | translate }}
        </ion-button>
      </div>
      <p *ngIf="!canComment" class="no-comment-permission">
        {{ 'PLAYLISTS.SIGN_IN_COMMENT_MSG' | translate }}
      </p>

      <!-- Comments list -->
      <div *ngIf="commentsLoading" class="comments-loading">
        <ion-spinner name="crescent" color="medium"></ion-spinner>
      </div>
      <div *ngIf="!commentsLoading && comments.length === 0" class="no-comments">
        {{ 'PLAYLISTS.NO_COMMENTS' | translate }}
      </div>
      <div class="comments-list">
        <div *ngFor="let comment of comments" class="comment-item">
          <div class="comment-header">
            <span class="comment-author">{{ comment.displayName }}</span>
            <span class="comment-date">{{ comment.createdAt.toDate() | date:'MMM d, y' }}</span>
            <div *ngIf="comment.uid === currentUser?.uid" class="comment-actions">
              <ion-button fill="clear" size="small" (click)="startEditComment(comment)" aria-label="Edit comment">
                <ion-icon name="pencil-outline" slot="icon-only"></ion-icon>
              </ion-button>
              <ion-button fill="clear" size="small" color="danger" (click)="deleteComment(comment)" aria-label="Delete comment">
                <ion-icon name="trash-outline" slot="icon-only"></ion-icon>
              </ion-button>
            </div>
          </div>
          <ng-container *ngIf="editingCommentId === comment.id; else commentView">
            <ion-textarea
              [(ngModel)]="editingCommentText"
              [autoGrow]="true"
              rows="2"
              maxlength="1000"
              class="comment-textarea edit-textarea">
            </ion-textarea>
            <div class="edit-actions">
              <ion-button size="small" (click)="saveEditComment()" [disabled]="!editingCommentText.trim()">
                {{ 'SETTINGS.OK' | translate }}
              </ion-button>
              <ion-button size="small" fill="clear" color="medium" (click)="cancelEditComment()">
                {{ 'SETTINGS.CANCEL' | translate }}
              </ion-button>
            </div>
          </ng-container>
          <ng-template #commentView>
            <p class="comment-text">{{ comment.text }}</p>
          </ng-template>
        </div>
      </div>
    </div>

    <!-- Lyrics panel -->
    <div *ngIf="openLyricsSong" class="notes-panel lyrics-panel">
      <div class="lyrics-panel-header-section">
        <div class="notes-panel-header">
          <div class="notes-panel-title">
            <ion-icon name="musical-notes-outline"></ion-icon>
            <span>{{ 'PLAYLISTS.LYRICS' | translate }}</span>
          </div>
          <ion-button fill="clear" (click)="closeLyrics()" aria-label="Close lyrics">
            <ion-icon name="close-outline" slot="icon-only"></ion-icon>
          </ion-button>
        </div>
        <p class="notes-song-title">{{ openLyricsSong.title }}</p>
      </div>

      <div *ngIf="lyricsLoading" class="lyrics-panel-status">
        <ion-spinner name="crescent" color="medium"></ion-spinner>
      </div>

      <div *ngIf="!lyricsLoading && !lyricsHasSegments" class="lyrics-panel-status">
        {{ lyricsError || ('PLAYLISTS.NO_LYRICS' | translate) }}
      </div>

      <ng-container *ngIf="!lyricsLoading && lyricsHasSegments">
        <div class="lyrics-panel-toolbar">
          <ion-button fill="clear" size="small" (click)="toggleLyricsTranslations()" [disabled]="lyricsTranslating">
            <ion-spinner *ngIf="lyricsTranslating" name="crescent" slot="start" style="width:14px;height:14px;margin-right:4px;"></ion-spinner>
            <span *ngIf="!lyricsTranslating">
              {{ (lyricsTranslationsVisible ? 'PLAYLISTS.HIDE_TRANSLATIONS' : 'PLAYLISTS.SHOW_TRANSLATIONS') | translate }}
            </span>
            <span *ngIf="lyricsTranslating">{{ 'PLAYLISTS.TRANSLATING' | translate }}</span>
          </ion-button>
        </div>
        <div class="lyrics-panel-scroll">
          <div *ngFor="let seg of openLyricsSong.transcriptSegments; let i = index"
            class="lyric-line"
            [class.active]="lyricsActiveSegmentIndex === i"
            (click)="seekLyricsToTime(seg.start)"
            role="button" tabindex="0"
            (keydown.enter)="seekLyricsToTime(seg.start)">
            <span class="lyric-time">{{ lyricsFormatTime(seg.start) }}</span>
            <div class="lyric-text-wrapper">
              <span class="lyric-text">{{ seg.text }}</span>
              <span *ngIf="lyricsTranslationsVisible && lyricsTranslations[i]" class="lyric-translation">{{ lyricsTranslations[i] }}</span>
            </div>
          </div>
        </div>
      </ng-container>
    </div>

  </div>
</ion-content>

<div *ngIf="canSubmit" class="add-song-fab-wrapper">
  <ion-button (click)="openSubmitModal()" shape="round">
    <ion-icon name="add" slot="start"></ion-icon>
    {{ 'PLAYLISTS.ADD_SONG' | translate }}
  </ion-button>
</div>
